import React, {Children, useCallback, useEffect, useRef, useState} from "react";
import {useLocation} from "react-router-dom";
import css from "./FilterBar.module.scss";
import Button from "../../Button/Button.jsx";
import PropTypes from "prop-types";
import Download02 from "../../Icons/Download02.jsx";
import Filter from "../../Icons/Filter.jsx";
import ArrowRefresh01 from "../../Icons/ArrowRefresh01.jsx";
import AppliedFilters from "./AppliedFilters.jsx";
import FiltersSidePanel from "./FiltersSidePanel.jsx";
import FilterBarItem from "./FilterBarItem.jsx";
import FilterBarPillButton from "./FilterBarPillButton.jsx";
import ColumnHorizontal01Icon from "../../Icons/ColumnHorizontal01Icon.jsx";
import MultiSelect from "../../FormElements/MultiSelect/MultiSelect.jsx";
import {encodeFilters, readFiltersFromUrl} from "../../../utils/filterUrlEncoding.js";
import {useColumnVisibilityContext} from "../../../providers/ColumnVisibilityProvider.jsx";
import {useTrackedHotkeys} from "../../../hooks/useTrackedHotkeys.jsx";

// Filter values can be objects (e.g. dateRange), and consumers rebuild them on every
// render, so they must be compared by value — a reference check always reports a change.
const isSameFilterValue = (a, b) => {
  if (a === b) {
    return true;
  }

  return JSON.stringify(a) === JSON.stringify(b);
}

const FiltersBarWithSidePanel = ({
  children,
  onChange,
  onApply,
  onUpdate,
  onPdfExport,
  onCsvExport,
  onExcelExport,
  onPrint,
  onExportAll,
  onExcelExportAll,
  onRefresh,
  className,
  trackingContext,
  posthog,
  externalAppliedFilters,
  onUrlStateChange,
  alwaysShowPanel = false,
  forceInline = false,
}) => {
  const location = useLocation();
  const childrenArray = Children.toArray(children);
  const availableFilterItems = childrenArray.filter(child => child.type && child.type === FilterBarItem)
  const pillButtons = childrenArray.filter(child => child.type && (child.type === FilterBarPillButton || child.type.__IS_FILTER_BAR_PILL_BUTTON))
  const getDefaultValues = (items) => {
    return items.reduce((acc, child) => {
        if (child.props.defaultValue) {
          acc[child.props.name] = child.props.defaultValue;
        }

        return acc;
      }, {});
  }

  const initFilters = (items) => {
    const filtersFromUrl = readFiltersFromUrl();

    return filtersFromUrl || getDefaultValues(items);
  }

  const publishCountRef = useRef(0);

  const notifyUrlStateChange = useCallback((filters) => {
    if (!onUrlStateChange) {
      return;
    }
    const params = new URLSearchParams(window.location.search);
    const encodedFilters = encodeFilters(filters);
    if (params.get('filters') === encodedFilters) {
      return;
    }
    publishCountRef.current += 1;
    params.set('filters', encodedFilters);
    onUrlStateChange(params);
  }, [onUrlStateChange]);

  const [appliedFilters, setAppliedFilters] = useState(() => initFilters(availableFilterItems));
  const [filtersPanelOpen, setFiltersPanelOpen] = useState(false);
  const [exportOpen, setExportOpen] = useState(false);
  const exportBtnRef = useRef();
  const [panelFilters, setPanelFilters] = useState(appliedFilters);
  const appliedFiltersRef = useRef(appliedFilters);
  appliedFiltersRef.current = appliedFilters;
  const panelEditedKeysRef = useRef(new Set());

  const capture = (action, properties = null) => {
    if (trackingContext) {
      posthog.capture(trackingContext+':filters:'+action, properties)
    }
  };

  const openFiltersPanel = () => {
    setFiltersPanelOpen(true);
    panelEditedKeysRef.current = new Set();
    setPanelFilters(appliedFilters);
    capture('panel_open')
  };

  const handleChange = (key, value) => {
    if (filtersPanelOpen) {
      let nextDraft = {...panelFilters, [key]: value};
      if (onUpdate) {
        nextDraft = onUpdate(key, value, {...panelFilters});
      }
      panelEditedKeysRef.current.add(key);
      Object.keys(nextDraft).forEach(draftKey => {
        if (!isSameFilterValue(nextDraft[draftKey], panelFilters[draftKey])) {
          panelEditedKeysRef.current.add(draftKey);
        }
      });
      setPanelFilters(nextDraft);
      // If onUpdate is provided is enabled, call onChange so parent can react and update filter options
      if (onUpdate) {
        onChange(nextDraft);
      }
      return;
    }

    const next = {...appliedFilters, [key]: value};
    setAppliedFilters(next);
    onChange(next);
    notifyUrlStateChange(next);
  };

  const handleApply = () => {
    capture('apply')
    const nextApplied = filtersPanelOpen ? panelFilters : appliedFilters;
    panelEditedKeysRef.current = new Set();
    setAppliedFilters(nextApplied);
    if (onApply) {
      onApply(nextApplied);
    } else {
      onChange(nextApplied);
    }
    setFiltersPanelOpen(false);
    notifyUrlStateChange(nextApplied);
  };

  const handleClearAll = () => {
    capture('clear_all')
    const publishesBeforeClear = publishCountRef.current;
    setAppliedFilters({})
    onChange({})
    setTimeout(() => {
      if (publishCountRef.current !== publishesBeforeClear) {
        return;
      }
      notifyUrlStateChange(appliedFiltersRef.current);
    }, 0);
  }

  const handleRemove = (filterName) => {
    capture('remove', {
      filter: filterName,
    })
    const newFilters = {...appliedFilters};
    delete newFilters[filterName];
    setAppliedFilters(newFilters);
    onChange(newFilters);
    notifyUrlStateChange(newFilters);
  };

  const handleAppliedFilterChipDropdownChange = (filterName, value) => {
    capture('inline_change', {
      filter: filterName,
    })
    const newFilters = {...appliedFilters};
    newFilters[filterName] = value
    setAppliedFilters(newFilters);
    onChange(newFilters);
    notifyUrlStateChange(newFilters);
  };

  const handleColumnsApply = (visibleCount, totalCount) => {
    capture('columns_update', {visibleCount, totalCount});
  };

  const handleExport = (format) => {
    capture('export', {
      format: format,
    })

    setExportOpen(false);
    if (format === 'pdf' && onPdfExport) onPdfExport();
    if (format === 'csv' && onCsvExport) onCsvExport();
    if (format === 'excel' && onExcelExport) onExcelExport();
    if (format === 'print' && onPrint) onPrint();
    if (format === 'exportAll' && onExportAll) onExportAll();
    if (format === 'excelExportAll' && onExcelExportAll) onExcelExportAll();
  };

  useEffect(() => {
    if (!exportOpen) return;
    const handle = (e) => {
      if (exportBtnRef.current && !exportBtnRef.current.contains(e.target)) {
        setExportOpen(false);
      }
    }
    document.addEventListener('mousedown', handle);
    return () => document.removeEventListener('mousedown', handle);
  }, [exportOpen]);

  const showInlineInputs = !alwaysShowPanel && (forceInline || Children.count(children) <= 1);
  const exportOptions = [
    onPdfExport && {value: 'pdf', label: 'PDF'},
    onCsvExport && {value: 'csv', label: 'CSV'},
    onExcelExport && {value: 'excel', label: 'Excel'},
    onPrint && {value: 'print', label: 'Print'},
    onExportAll && {value: 'exportAll', label: onExcelExportAll ? 'Export All (CSV)' : 'Export All'},
    onExcelExportAll && {value: 'excelExportAll', label: 'Export All (Excel)'},
  ].filter(Boolean);

  useEffect(() => {
    // Only proceed if externalAppliedFilters is valid
    if (externalAppliedFilters) {
      const updatedFilters = { ...appliedFilters };
      let changed = false;

      Object.keys(externalAppliedFilters).forEach((key) => {
        if (
          externalAppliedFilters[key] !== undefined &&
          !isSameFilterValue(externalAppliedFilters[key], appliedFilters[key])
        ) {
          updatedFilters[key] = externalAppliedFilters[key];
          changed = true;
        }
      });

      if (changed) {
        setAppliedFilters(updatedFilters);
        notifyUrlStateChange(updatedFilters);
      }
    }
  }, [externalAppliedFilters, appliedFilters, notifyUrlStateChange]);

  useEffect(() => {
    onChange(appliedFilters);
  }, [])

  const urlFilters = new URLSearchParams(location.search).get('filters');
  useEffect(() => {
    if (!onUrlStateChange) {
      return;
    }

    const restoredFilters = initFilters(availableFilterItems);
    if (encodeFilters(restoredFilters) === encodeFilters(appliedFilters)) {
      return;
    }

    setAppliedFilters(restoredFilters);
    panelEditedKeysRef.current = new Set();
    setPanelFilters(restoredFilters);
    if (onApply) {
      onApply(restoredFilters);
    } else {
      onChange(restoredFilters);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [urlFilters]);

  // Keep side panel filters in sync with applied filters when panel is open, but only for the keys that have not been edited in the panel.
  useEffect(() => {
    if (!filtersPanelOpen) {
      return;
    }

    setPanelFilters(previousDraft => {
      let nextDraft = previousDraft;
      Object.keys(appliedFilters).forEach(key => {
        if (panelEditedKeysRef.current.has(key)) {
          return;
        }
        if (isSameFilterValue(appliedFilters[key], previousDraft[key])) {
          return;
        }
        if (nextDraft === previousDraft) {
          nextDraft = {...previousDraft};
        }
        nextDraft[key] = appliedFilters[key];
      });

      return nextDraft;
    });
  }, [appliedFilters, filtersPanelOpen])

  const renderedPills = pillButtons.length > 0 ? (
    <div style={{display: 'flex', gap: '8px'}}>
      {pillButtons.map((pill, idx) =>
        React.cloneElement(pill, {key: idx}))
      }
    </div>
  ) : null;

  useTrackedHotkeys('f', openFiltersPanel, 'filters:side_panel_view', {enabled: !showInlineInputs})

  return (
    <div className={className}>
      <FiltersBar
        showInlineInputs={showInlineInputs}
        availableFilterItems={availableFilterItems}
        appliedFilters={appliedFilters}
        onChange={handleChange}
        onRemove={handleRemove}
        onClearAll={handleClearAll}
        onDropdownChange={handleAppliedFilterChipDropdownChange}
        openFiltersPanel={openFiltersPanel}
        exportOptions={exportOptions}
        handleExportOption={handleExport}
        onAppliedFilterChipClick={() => setFiltersPanelOpen(true)}
        quickFilters={renderedPills}
        onRefresh={onRefresh}
        onColumnsApply={handleColumnsApply}
      />
      <FiltersSidePanel
        showInlineInputs={showInlineInputs}
        filtersPanelOpen={filtersPanelOpen}
        availableFilterItems={availableFilterItems}
        appliedFilters={panelFilters}
        onChange={handleChange}
        onClose={() => setFiltersPanelOpen(false)}
        onApply={handleApply}
        quickFilters={renderedPills}
      />
    </div>
  );
};

const FiltersBar = ({
  showInlineInputs,
  availableFilterItems,
  appliedFilters,
  onChange,
  onRemove,
  onClearAll,
  onDropdownChange,
  openFiltersPanel,
  exportOptions,
  handleExportOption,
  onAppliedFilterChipClick,
  quickFilters,
  onRefresh,
  onColumnsApply,
}) => {
  const {columnsVisibilityOptions} = useColumnVisibilityContext();

  const hasActions = exportOptions.length > 0 || Boolean(onRefresh) || columnsVisibilityOptions?.length > 0;
  const isBareInlineBar = showInlineInputs && !hasActions;

  return (
    <div className={`${css.filtersBar} ${isBareInlineBar && css.inlineInputsFiltersBar}`}>
      {quickFilters}
      {quickFilters && (
        <span
          aria-hidden
          style={{
            width: '1px',
            alignSelf: 'stretch',
            background: 'var(--grey-8)',
            margin: '0 12px',
            display: 'inline-block',
            borderRadius: '1px',
            opacity: 0.9,
          }}
        />
      )}
      <AppliedFilters
        showInlineInputs={showInlineInputs}
        availableFilterItems={availableFilterItems}
        appliedFilters={appliedFilters}
        onChange={onChange}
        onRemove={onRemove}
        onClearAll={onClearAll}
        onDropdownChange={onDropdownChange}
        onAppliedFilterChipClick={onAppliedFilterChipClick}
      />
      <FilterButtonsSection
        showInlineInputs={showInlineInputs}
        openFiltersPanel={openFiltersPanel}
        exportOptions={exportOptions}
        handleExportOption={handleExportOption}
        onRefresh={onRefresh}
        onColumnsApply={onColumnsApply}
      />
    </div>
  );
};

const FilterButtonsSection = ({
  showInlineInputs,
  openFiltersPanel,
  exportOptions,
  handleExportOption,
  onRefresh,
  onColumnsApply,
}) => {
  return (
    <div className={`${css.buttonsSection} ${css.noPrint}`}>
      {!showInlineInputs &&
        <Button size="small" onClick={openFiltersPanel} icon={<Filter />}>
          Filters
        </Button>
      }
      <ColumnVisibility onColumnsApply={onColumnsApply} />
      {exportOptions.length > 0 &&
        <ExportDropdown
          options={exportOptions}
          onSelect={handleExportOption}
        />
      }
      {onRefresh &&
        <Button
          size="small"
          icon={<ArrowRefresh01 />}
          onClick={onRefresh}
          variant="tertiary"
        />
      }
    </div>
  );
};

const ExportDropdown = ({options, onSelect}) => {
  const [open, setOpen] = useState(false);
  const ref = useRef();

  useEffect(() => {
    if (!open) return;
    const handleClickOutside = (e) => {
      if (ref.current && !ref.current.contains(e.target)) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [open]);

  return (
    <div ref={ref}>
      <Button
        size="small"
        variant="tertiary"
        onClick={() => setOpen((prev) => !prev)}
      >
        <Download02 color="var(--grey-64)"/>
      </Button>
      {open && (
        <div className={css.exportDropdownMenu} role="listbox">
          {options.map((opt) =>
            <button
              key={opt.value}
              className={css.exportDropdownItem}
              onClick={() => {
                onSelect(opt.value);
                setOpen(false);
              }}
            >
              {opt.label}
            </button>
          )}
        </div>
      )}
    </div>
  );
};

const ColumnVisibility = ({onColumnsApply}) => {
  const {visibleColumns, applyColumns, columnsVisibilityOptions} = useColumnVisibilityContext();
  const [open, setOpen] = useState(false);
  const ref = useRef();
  const [selectedColumns, setSelectedColumns] = useState(visibleColumns);

  useEffect(() => {
    setSelectedColumns(visibleColumns);
  }, [visibleColumns]);

  useEffect(() => {
    if (!open) return;
    const handleClickOutside = (e) => {
      if (ref.current && !ref.current.contains(e.target)) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [open]);

  if (!columnsVisibilityOptions?.length) {
    return null;
  }

  const onConfirmColumnVisibility = () => {
    const hasChanged = selectedColumns.length !== visibleColumns.length
      || selectedColumns.some(column => !visibleColumns.includes(column));
    if (hasChanged) {
      onColumnsApply(selectedColumns.length, columnsVisibilityOptions.length);
    }
    applyColumns(selectedColumns);
    setOpen(false);
  }

  const handleSelectAllColumns = () => {
    setSelectedColumns(columnsVisibilityOptions.map(opt => opt.value));
  }

  return (
    <div ref={ref}>
      <Button
        size="small"
        variant="tertiary"
        onClick={() => setOpen((prev) => !prev)}
      >
        <ColumnHorizontal01Icon color="var(--grey-64)"/>
      </Button>
      {open &&
        <div className={css.columnsVisibilityMenu}>
          <MultiSelect
            label="Columns to show:"
            onChange={(evt) => setSelectedColumns(evt.target.value)}
            value={selectedColumns}
            options={columnsVisibilityOptions}
            onSelectAll={handleSelectAllColumns}
          />
          <Button
            variant="primary"
            size="small"
            onClick={onConfirmColumnVisibility}
          >
            Confirm
          </Button>
        </div>
      }
    </div>
  );
};

FiltersBarWithSidePanel.propTypes = {
  children: PropTypes.node.isRequired,
  appliedFilters: PropTypes.objectOf(
    PropTypes.oneOfType([
      PropTypes.string,
      PropTypes.bool,
      PropTypes.shape({
        startDate: PropTypes.string,
        endDate: PropTypes.string,
      }),
    ])
  ),
  onChange: PropTypes.func,
  onPdfExport: PropTypes.func,
  onCsvExport: PropTypes.func,
  onExcelExport: PropTypes.func,
  onPrint: PropTypes.func,
  onExportAll: PropTypes.func,
  onExcelExportAll: PropTypes.func,
  onRefresh: PropTypes.func,
  className: PropTypes.string,
  onUrlStateChange: PropTypes.func,
  alwaysShowPanel: PropTypes.bool,
  forceInline: PropTypes.bool,
};

export default FiltersBarWithSidePanel;
